import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Typography, Input,
  Space, Spin, Badge, DatePicker, Button, Tooltip,
} from 'antd';
import {
  DollarOutlined, ShoppingCartOutlined, CheckCircleOutlined,
  SearchOutlined, ReloadOutlined, TrophyOutlined,
} from '@ant-design/icons';
import { govtAdminAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PurchasesPage: React.FC = () => {
  const [loading, setLoading]     = useState(true);
  const [purchases, setPurchases] = useState<any[]>([]);
  const [summary, setSummary]     = useState<any>(null);
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const [total, setTotal]         = useState(0);
  const PAGE_SIZE = 50;

  const load = useCallback(async (pg = page, q = search) => {
    setLoading(true);
    try {
      const res = await govtAdminAPI.getSeriesPurchases({ page: pg, pageSize: PAGE_SIZE, search: q });
      const data = res.data;
      setPurchases(data.purchases ?? []);
      setSummary(data.summary ?? null);
      setTotal(data.summary?.total_purchases ?? 0);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(1, ''); }, []);

  const handleSearch = () => { setPage(1); load(1, search); };

  const columns = [
    {
      title: 'Student',
      dataIndex: 'user_name',
      render: (name: string, r: any) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{name || '—'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.user_email}</Text>
        </Space>
      ),
    },
    {
      title: 'Series',
      dataIndex: 'series_title',
      render: (title: string, r: any) => (
        <Space direction="vertical" size={0}>
          <Space>
            {r.accent_color && (
              <span style={{
                display: 'inline-block', width: 10, height: 10,
                borderRadius: '50%', background: r.accent_color,
              }} />
            )}
            <Text strong style={{ fontSize: 13 }}>{title}</Text>
          </Space>
          {r.exam_name && <Text type="secondary" style={{ fontSize: 12 }}>{r.exam_name}</Text>}
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (amt: number) => (
        <Text strong style={{ color: '#059669', fontSize: 14 }}>
          ₹{Number(amt).toFixed(0)}
        </Text>
      ),
      sorter: (a: any, b: any) => Number(a.amount) - Number(b.amount),
    },
    {
      title: 'Date',
      dataIndex: 'purchased_at',
      render: (d: string) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>{dayjs(d).format('DD MMM YYYY')}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(d).format('hh:mm A')}</Text>
        </Space>
      ),
      sorter: (a: any, b: any) => new Date(a.purchased_at).getTime() - new Date(b.purchased_at).getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Payment ID',
      dataIndex: 'payment_id',
      render: (id: string) => id
        ? <Tooltip title={id}><Text code style={{ fontSize: 11 }}>{id.slice(-10)}</Text></Tooltip>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      render: (active: boolean) => (
        <Badge
          status={active ? 'success' : 'default'}
          text={<Text style={{ fontSize: 12 }}>{active ? 'Active' : 'Expired'}</Text>}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: '0 4px' }}>
      <Space style={{ marginBottom: 20 }} align="center">
        <TrophyOutlined style={{ fontSize: 22, color: '#D97706' }} />
        <Title level={3} style={{ margin: 0 }}>Purchases & Revenue</Title>
      </Space>

      {/* Summary cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 14, background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)', color: '#fff' }}>
            <Statistic
              title={<Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>Total Revenue</Text>}
              value={summary ? Number(summary.total_revenue).toFixed(0) : 0}
              prefix={<DollarOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff', fontSize: 28, fontWeight: 700 }}
              formatter={(v) => `₹${v}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 14 }}>
            <Statistic
              title="Total Purchases"
              value={summary?.total_purchases ?? 0}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#1D4ED8', fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 14 }}>
            <Statistic
              title="Active Access"
              value={summary?.active_count ?? 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#059669', fontWeight: 700 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Card bordered={false} style={{ borderRadius: 14 }}>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Search student or series..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 280 }}
            allowClear
          />
          <Button icon={<SearchOutlined />} type="primary" onClick={handleSearch}>Search</Button>
          <Button icon={<ReloadOutlined />} onClick={() => { setSearch(''); setPage(1); load(1, ''); }}>Reset</Button>
          <Tag color="blue">{total} total purchases</Tag>
        </Space>

        <Spin spinning={loading}>
          <Table
            dataSource={purchases}
            columns={columns}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              onChange: (pg) => { setPage(pg); load(pg, search); },
              showSizeChanger: false,
            }}
            size="middle"
            scroll={{ x: 800 }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default PurchasesPage;
